import { useId, useState } from "react";
import { CoreModal, CorePaper, CoreTitleCard } from "../../core";
import { ProctoringOptionProfileV2, OptionRelatedInstituteSettings } from "@verity/proctoring-option/types";
import { VerityInstituteSettings } from "../..";
import i18nPackage from "../../i18n/i18n";
import ProfileSection from "../profileSection/profileSection";
import { useMobileLayout } from "../../hooks";
import { ConditionalWrapper } from "../../wrapper";
import styles from "./selectProfile.module.css";

export type SelectProfileModalProps = {
	open: boolean;
	profileList: Array<ProctoringOptionProfileV2>;
	refreshProfiles: () => Promise<void | Map<number, ProctoringOptionProfileV2>>;
	activeProfileId: number | undefined;
	setActiveProfileId: (profileId: number | undefined) => void;
	loadProfile: (profile: ProctoringOptionProfileV2) => void;
	lmsCourseId?: string;
	optionRelatedInstituteSettings?: OptionRelatedInstituteSettings;
	verityInstituteSettings?: VerityInstituteSettings;
	enforcedProfilesOnly: boolean;
	hasInstituteProfilePermissions: boolean;
	onClose: () => void;
};

const SelectProfileModal = ({
	open,
	profileList,
	refreshProfiles,
	activeProfileId,
	setActiveProfileId,
	loadProfile,
	lmsCourseId,
	optionRelatedInstituteSettings,
	verityInstituteSettings,
	enforcedProfilesOnly,
	hasInstituteProfilePermissions,
	onClose,
}: SelectProfileModalProps) => {
	const radioGroupAriaId = useId();
	const [selectedProfileId, setSelectedProfileId] = useState(activeProfileId);
	const isMobileLayout = useMobileLayout();

	return (
		<CoreModal
			open={open}
			onClose={onClose}
			title={isMobileLayout ? undefined : i18nPackage.t("editQuizPage.selectProfile.title")}
			minWidth={isMobileLayout ? undefined : "min(60%, 1000px)"}
			minHeight={isMobileLayout ? undefined : "min(85%, 624px)"}
			widthFixed
			heightFixed={isMobileLayout}
			headerId={radioGroupAriaId}
			padding={isMobileLayout ? undefined : "32px 32px 4px 32px"}
			fullScreen={isMobileLayout}
			showTopCloseButton={!isMobileLayout}
			dialogContentBackground={isMobileLayout ? "#f4f4f4" : undefined}
			buttons={[
				{
					text: i18nPackage.t("editQuizPage.selectProfile.cancel"),
					color: "secondary",
					style: { marginLeft: isMobileLayout ? "auto" : undefined },
					onClick: () => {
						if (selectedProfileId === undefined) {
							setActiveProfileId(undefined);
						}
						onClose();
					},
				},
				{
					text: i18nPackage.t("editQuizPage.selectProfile.select"),
					color: "primary",
					onClick: () => {
						if (selectedProfileId !== undefined) {
							const activeProfile = profileList.find((profile) => profile.id === selectedProfileId);
							if (activeProfile) {
								setActiveProfileId(activeProfile.id);
								loadProfile(activeProfile);
							}
						} else {
							setActiveProfileId(undefined);
						}
						onClose();
					},
				},
			]}
		>
			<div className={`${styles.pageContainer} ${isMobileLayout ? styles.pageContainerCompact : ""}`}>
				{isMobileLayout && (
					<CoreTitleCard
						title={i18nPackage.t("editQuizPage.platformStep.savedProfiles.selectProfile")}
						description={i18nPackage.t("editQuizPage.platformStep.savedProfiles.selectProfileDescription")}
						style={{ padding: "15px 10px 10px 10px" }}
						slotProps={{ title: { style: { color: "#42296E" } }, text: { style: { gap: "5px" } } }}
					/>
				)}
				<ConditionalWrapper
					condition={isMobileLayout}
					whenTrue={(children) => (
						<CorePaper component="section" sx={{ padding: "10px" }} className={styles.profilePage}>
							{children}
						</CorePaper>
					)}
				>
					<ProfileSection
						profileList={profileList}
						selectedProfileId={selectedProfileId}
						setSelectedProfileId={setSelectedProfileId}
						refreshProfiles={refreshProfiles}
						lmsCourseId={lmsCourseId}
						optionRelatedInstituteSettings={optionRelatedInstituteSettings}
						verityInstituteSettings={verityInstituteSettings}
						hasInstituteProfilePermissions={hasInstituteProfilePermissions}
						enforcedProfilesOnly={enforcedProfilesOnly}
						radioGroupAriaId={radioGroupAriaId}
					/>
				</ConditionalWrapper>
			</div>
		</CoreModal>
	);
};

export default SelectProfileModal;
